(() => {
  'use strict';

  const FORM_SELECTOR = 'form[data-led-search-form]';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 220;
  const RESULT_LIMIT = 8;
  const formStates = new WeakMap();

  // ---- Part-number matching -------------------------------------------------
  // Customers often type just the number ("9013", "164L", "820LEU", "2050R-1")
  // without the "LG" prefix. Shopify search matches from the start of a word,
  // so "9013" never finds "LG9013". These helpers add the prefix for them.
  const PART_NUMBER_SUGGEST = /^\d{2,5}[a-z]{0,3}(?:-\d{1,2})?$/i; // live dropdown (2+ digits)
  // Full search page (3+ digits): only the suffixes LG SKUs actually use, so "1080p" etc. stay untouched.
  const PART_NUMBER_SUBMIT = /^\d{3,5}(?:L|R|B|K|EU|LEU|REU)?(?:-\d{1,2})?$/i;

  const toPartNumber = (query) => `LG${query.toUpperCase()}`;
  const isSuggestPartNumber = (query) => PART_NUMBER_SUGGEST.test(query);
  const isSubmitPartNumber = (query) => PART_NUMBER_SUBMIT.test(query);

  // If someone lands on /search?q=9013 (e.g. from the search page's own form),
  // send them straight to the LG-prefixed results.
  (() => {
    try {
      if (!/\/search\/?$/.test(window.location.pathname)) return;
      const url = new URL(window.location.href);
      const q = (url.searchParams.get('q') || '').trim();
      if (!q || !isSubmitPartNumber(q)) return;
      url.searchParams.set('q', toPartNumber(q));
      if (!url.searchParams.has('options[prefix]')) url.searchParams.set('options[prefix]', 'last');
      window.location.replace(url.toString());
    } catch (error) {
      // Never block the page if this fails.
    }
  })();
  // ---------------------------------------------------------------------------

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const getQuery = (form) => {
    const input = form.querySelector('input[name="q"]');
    return input ? input.value.trim() : '';
  };

  const getSearchUrl = (form, query) => {
    const url = new URL(form.action || '/search', window.location.origin);
    url.searchParams.set('q', isSubmitPartNumber(query) ? toPartNumber(query) : query);

    form.querySelectorAll('input[type="hidden"][name]').forEach((input) => {
      if (input.value) url.searchParams.set(input.name, input.value);
    });

    return url.toString();
  };

  const goToSearch = (form) => {
    const input = form.querySelector('input[name="q"]');
    const query = getQuery(form);

    if (!query) {
      input?.focus();
      return;
    }

    window.location.assign(getSearchUrl(form, query));
  };

  const getSuggestUrl = (query) => {
    let base = window.routes?.predictive_search_url || '/search/suggest';
    if (!/\.json(?:$|\?)/.test(base)) base = `${base.replace(/\/$/, '')}.json`;

    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(RESULT_LIMIT));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,vendor,variants.sku,variants.title,tag'
    );
    return url.toString();
  };

  const getProductImage = (product) => {
    const image = product?.image;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  };

  const getProductSku = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((variant) => variant?.sku)?.sku || '';
  };

  const ensureResultsPanel = (form) => {
    let state = formStates.get(form);
    if (state) return state;

    const panel = document.createElement('div');
    panel.className = 'led-search-live-results';
    panel.hidden = true;
    panel.setAttribute('role', 'listbox');
    panel.setAttribute('aria-label', 'Search suggestions');
    document.body.appendChild(panel);

    state = {
      panel,
      timer: null,
      controller: null,
      requestNumber: 0,
    };
    formStates.set(form, state);
    return state;
  };

  const positionResultsPanel = (form) => {
    const state = formStates.get(form);
    if (!state || state.panel.hidden) return;

    const input = form.querySelector('input[name="q"]');
    const field = form.querySelector('.field') || form;
    const anchor = input || field;
    const rect = anchor.getBoundingClientRect();

    state.panel.style.left = `${Math.max(8, rect.left + window.scrollX)}px`;
    state.panel.style.top = `${rect.bottom + window.scrollY + 6}px`;
    state.panel.style.width = `${Math.max(280, rect.width)}px`;
    state.panel.style.maxWidth = `${Math.max(280, window.innerWidth - 16)}px`;
  };

  const hideResults = (form) => {
    const state = formStates.get(form);
    if (!state) return;
    state.panel.hidden = true;
    state.panel.innerHTML = '';
    form.querySelector('input[name="q"]')?.setAttribute('aria-expanded', 'false');
  };

  const renderResults = (form, query, products) => {
    const state = ensureResultsPanel(form);
    const input = form.querySelector('input[name="q"]');
    const localeIsGerman = document.documentElement.lang?.toLowerCase().startsWith('de');

    const productMarkup = products.length
      ? products
          .map((product) => {
            const title = escapeHtml(product.title || '');
            const sku = escapeHtml(getProductSku(product));
            const image = getProductImage(product);
            const imageMarkup = image
              ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="54" height="54">`
              : '<span class="led-search-live-results__placeholder" aria-hidden="true"></span>';
            const url = new URL(product.url || '/search', window.location.origin).toString();

            return `
              <a class="led-search-live-results__item" href="${escapeHtml(url)}" role="option">
                <span class="led-search-live-results__media">${imageMarkup}</span>
                <span class="led-search-live-results__copy">
                  <strong>${title}</strong>
                  ${sku ? `<small>SKU: ${sku}</small>` : ''}
                </span>
              </a>`;
          })
          .join('')
      : `<div class="led-search-live-results__empty">${
          localeIsGerman ? 'Keine passenden Produkte gefunden.' : 'No matching products found.'
        }</div>`;

    const fullSearchUrl = getSearchUrl(form, query);
    const searchLabel = localeIsGerman ? `Alle Ergebnisse für „${escapeHtml(query)}“` : `View all results for “${escapeHtml(query)}”`;

    state.panel.innerHTML = `
      <div class="led-search-live-results__list">${productMarkup}</div>
      <a class="led-search-live-results__all" href="${escapeHtml(fullSearchUrl)}">${searchLabel}</a>`;
    state.panel.hidden = false;
    input?.setAttribute('aria-expanded', 'true');
    positionResultsPanel(form);
  };

  const fetchProducts = async (query, signal) => {
    const response = await fetch(getSuggestUrl(query), {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
    const data = await response.json();
    return data?.resources?.results?.products || [];
  };

  // Part-number matches first, then the normal matches, without duplicates.
  const mergeProducts = (...lists) => {
    const seen = new Set();
    const merged = [];
    lists.flat().forEach((product) => {
      const key = product?.id || product?.url || product?.handle;
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(product);
    });
    return merged.slice(0, RESULT_LIMIT);
  };

  const fetchResults = async (form, query) => {
    const state = ensureResultsPanel(form);
    state.controller?.abort();
    state.controller = new AbortController();
    const { signal } = state.controller;
    const requestNumber = ++state.requestNumber;

    try {
      let products;
      if (isSuggestPartNumber(query)) {
        const [partMatches, plainMatches] = await Promise.all([
          fetchProducts(toPartNumber(query), signal),
          fetchProducts(query, signal).catch(() => []),
        ]);
        products = mergeProducts(partMatches, plainMatches);
      } else {
        products = await fetchProducts(query, signal);
      }

      if (requestNumber !== state.requestNumber || getQuery(form) !== query) return;
      renderResults(form, query, products);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      // The normal search submission remains available even if live suggestions fail.
      hideResults(form);
    }
  };

  const scheduleSuggestions = (form) => {
    const state = ensureResultsPanel(form);
    const query = getQuery(form);
    clearTimeout(state.timer);

    if (query.length < MIN_QUERY_LENGTH) {
      state.controller?.abort();
      hideResults(form);
      return;
    }

    state.timer = window.setTimeout(() => fetchResults(form, query), DEBOUNCE_MS);
  };

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest(`${FORM_SELECTOR} input[name="q"]`) : null;
      if (!input) return;
      const form = input.closest(FORM_SELECTOR);
      if (form) scheduleSuggestions(form);
    },
    true
  );

  document.addEventListener(
    'focusin',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest(`${FORM_SELECTOR} input[name="q"]`) : null;
      if (!input) return;
      const form = input.closest(FORM_SELECTOR);
      if (form && getQuery(form).length >= MIN_QUERY_LENGTH) scheduleSuggestions(form);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest(`${FORM_SELECTOR} input[name="q"]`) : null;
      if (!input) return;
      const form = input.closest(FORM_SELECTOR);
      if (!form) return;

      if (event.key === 'Escape') {
        hideResults(form);
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        event.stopImmediatePropagation();
        goToSearch(form);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const submitButton = event.target instanceof Element
        ? event.target.closest(`${FORM_SELECTOR} button[type="submit"], ${FORM_SELECTOR} input[type="submit"]`)
        : null;

      if (submitButton) {
        const form = submitButton.closest(FORM_SELECTOR);
        if (form) {
          event.preventDefault();
          event.stopImmediatePropagation();
          goToSearch(form);
        }
        return;
      }

      document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
        const state = formStates.get(form);
        if (!state || state.panel.hidden) return;
        if (!form.contains(event.target) && !state.panel.contains(event.target)) hideResults(form);
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof Element ? event.target.closest(FORM_SELECTOR) : null;
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      goToSearch(form);
    },
    true
  );

  const repositionOpenPanels = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => positionResultsPanel(form));
  };

  window.addEventListener('resize', repositionOpenPanels, { passive: true });
  window.addEventListener('scroll', repositionOpenPanels, { passive: true });
})();
